/**
 * useGuestGate — gate any action behind authentication.
 *
 * Usage:
 *   const { gate, sheetOpen, sheetTitle, setSheetOpen, returnUrl } = useGuestGate();
 *
 *   <button onClick={() => gate("Join the conversation", () => handleLike(post.id))}>
 *
 * If the user is logged in, `gate(title, fn)` runs `fn()` immediately.
 * If not, it opens the QuickOTPModal in guest mode with the given title.
 *
 * On successful auth from the modal, the original `fn` is NOT replayed
 * automatically — the page typically re-renders with `user` populated
 * and the next user click naturally goes through. This matches the
 * spec ("Do not redirect guests… let them choose signup or login").
 *
 * Mar 1, 2026 — Built for Step 3 of the homepage migration.
 */
import { useState, useCallback } from 'react';
import { useAuth } from '@/App';

export function useGuestGate() {
  const { user } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetTitle, setSheetTitle] = useState('');

  const gate = useCallback((actionTitle, callback) => {
    if (user) {
      callback?.();
      return;
    }
    setSheetTitle(actionTitle || 'Sign up to continue');
    setSheetOpen(true);
  }, [user]);

  // Captured at the moment the modal opens so post-auth redirect
  // returns the user to exactly where they were.
  const returnUrl = typeof window !== 'undefined'
    ? window.location.pathname + window.location.search
    : '/';

  return { gate, sheetOpen, sheetTitle, setSheetOpen, returnUrl };
}
